import React, { useEffect, useState, useRef } from "react";
import { fetchJson, getStreamUrl } from "../../services/api";
import {
  PageHeader,
  SectionCard,
  Loader,
  ErrorMsg,
  refreshBtnStyle,
} from "../components/Common";
import "./VMContainersPage.css";

interface ContainerStat {
  container_id: string;
  name: string;
  cpu: string;
  memory_usage: string;
  memory_pct: string;
  net_io: string;
  block_io: string;
  pids: string;
}

const VMContainersPage: React.FC = () => {
  const [containers, setContainers] = useState<ContainerStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showLogs, setShowLogs] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState("");
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [details, setDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [streamMode, setStreamMode] = useState<string | null>(null); // 'sse' | 'poll' | null
  const [containerStreamMode, setContainerStreamMode] = useState<string | null>(null); // SSE mode for docker stats

  const closeModal = () => {
    setShowLogs(false);
    setShowDetails(false);
    setSelectedMetric(null);
  };

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      // `/vm/docker/` only returns the raw docker command output.  The stats
      // endpoint returns the structured data that this table renders.
      const res: any = await fetchJson("/monitor/vm/docker/stats/");
      if (!Array.isArray(res.containers)) {
        throw new Error("The Docker statistics response was invalid.");
      }
      setContainers(res.containers);
    } catch (e: any) {
      setError(e.message || "Unable to load Docker statistics.");
    } finally {
      setLoading(false);
    }
  };

  const loadContainerLogs = async (
    containerName: string
  ) => {
    try {
      setLogsLoading(true);
      setSelectedContainer(containerName);
      setShowLogs(true);
      setShowDetails(false);
      setDetails(null);
      setDetailsError(null);

      const res: any =
        await fetchJson(
          "/monitor/vm/container-logs/",
          { container: containerName }
        );

      setLogs(res.logs || []);
    } catch (err: any) {
      setLogs([
        err.message ||
          "Unable to load container logs.",
      ]);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadContainerDetails = async (
    containerName: string,
    metric: string = "general"
  ) => {
    try {
      setDetailsLoading(true);
      setSelectedContainer(containerName);
      setSelectedMetric(metric);
      setShowDetails(true);
      setShowLogs(false);
      setDetails(null);
      setDetailsError(null);

      const res: any = await fetchJson("/monitor/vm/container-details/", { container: containerName, metric });

      if (res.details?.error) {
        setDetailsError(res.details.error);
        setDetails(res.details);
      } else {
        let payload = res.details || null;

        // When opening the general view, also fetch live metric-specific data
        // so the modal can show current CPU/memory/network/pids values.
        if (metric === "general") {
          try {
            const [cpuRes, memRes, netRes, pidsRes] = (await Promise.all([
              fetchJson("/monitor/vm/container-details/", { container: containerName, metric: "cpu" }),
              fetchJson("/monitor/vm/container-details/", { container: containerName, metric: "memory" }),
              fetchJson("/monitor/vm/container-details/", { container: containerName, metric: "network" }),
              fetchJson("/monitor/vm/container-details/", { container: containerName, metric: "pids" }),
            ])) as any[];

            payload = {
              ...payload,
              stats: {
                ...(cpuRes?.details?.stats || {}),
                ...(memRes?.details?.stats || {}),
                ...(netRes?.details?.stats || {}),
                ...(pidsRes?.details?.stats || {}),
              },
              processes: pidsRes?.details?.processes || [],
            };
          } catch (e) {
            // ignore extra stats failure; still show inspect data
          }
        }

        setDetails(payload);
      }
    } catch (err: any) {
      setDetailsError(
        err.message ||
          "Unable to load container details."
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!showLogs && !showDetails) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showLogs, showDetails]);

  // EventSource ref for SSE streaming of live stats while details modal is open
  const statsEventSourceRef = useRef<EventSource | null>(null);
  const pollingRef = useRef<number | null>(null);
  const dockerStatsEventSourceRef = useRef<EventSource | null>(null); // For docker stats streaming

  const formatConfigValue = (v: any) => {
    if (v === null || v === undefined) return "Not set";
    if (typeof v === "number") return v === 0 ? "Not set" : String(v);
    if (typeof v === "string") return v === "" ? "Not set" : v;
    return String(v);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      try {
        window.clearInterval(pollingRef.current as number);
      } catch (e) {}
      pollingRef.current = null;
    }
    setStreamMode(null);
  };

  const startPolling = (container: string, metric: string | null) => {
    stopPolling();
    setStreamMode("poll");
    // Poll every 2000ms
  pollingRef.current = window.setInterval(async () => {
      try {
        if (!container) return;

        if (!metric || metric === "general") {
          const [cpuRes, memRes, netRes, pidsRes] = (await Promise.all([
            fetchJson("/monitor/vm/container-details/", { container, metric: "cpu" }),
            fetchJson("/monitor/vm/container-details/", { container, metric: "memory" }),
            fetchJson("/monitor/vm/container-details/", { container, metric: "network" }),
            fetchJson("/monitor/vm/container-details/", { container, metric: "pids" }),
          ])) as any[];

          setDetails((prev: any) => ({
            ...(prev || {}),
            stats: {
              ...(prev?.stats || {}),
              ...(cpuRes?.details?.stats || {}),
              ...(memRes?.details?.stats || {}),
              ...(netRes?.details?.stats || {}),
              ...(pidsRes?.details?.stats || {}),
            },
            processes: pidsRes?.details?.processes || prev?.processes || [],
          }));
        } else {
          const res: any = await fetchJson("/monitor/vm/container-details/", { container, metric: metric || "general" });
          setDetails((prev: any) => ({ ...(prev || {}), stats: { ...(prev?.stats || {}), ...(res?.details?.stats || {}) } }));
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 2000) as unknown as number;
  };

  useEffect(() => {
    // Start SSE when details modal is open for a container
        if (showDetails && selectedContainer) {
      try {
        const url = getStreamUrl("/monitor/vm/container-stats-stream/", { container: selectedContainer, interval: 2 });

        // Close existing if any
        if (statsEventSourceRef.current) {
          statsEventSourceRef.current.close();
        }

        const es = new EventSource(url);
        statsEventSourceRef.current = es;

        // stop any polling when SSE is active
        stopPolling();
        setStreamMode("sse");
        console.log("[Container SSE] Connected to:", url);

        es.onmessage = (e) => {
          try {
            console.log("[Container SSE] Received data:", e.data?.substring(0, 100));
            const data = JSON.parse(e.data);
            setDetails((prev: any) => {
              if (!prev) return { stats: data };
              return {
                ...prev,
                stats: {
                  ...(prev.stats || {}),
                  ...(data || {}),
                },
              };
            });
          } catch (err) {
            console.error("[Container SSE] Parse error:", err);
          }
        };

        es.onerror = () => {
          console.error("[Container SSE] Error, state:", es.readyState);
          // Close on error; fall back to polling
          try {
            es.close();
          } catch (e) {}
          statsEventSourceRef.current = null;
          startPolling(selectedContainer, selectedMetric);
          setStreamMode("poll");
        };
      } catch (err) {
        // SSE failed to open; start polling fallback
        startPolling(selectedContainer, selectedMetric);
      }
    }

    return () => {
      if (statsEventSourceRef.current) {
        try {
          statsEventSourceRef.current.close();
        } catch (e) {}
        statsEventSourceRef.current = null;
      }
      stopPolling();
    };
  }, [showDetails, selectedContainer]);

  useEffect(() => {
    // Start docker stats SSE when page loads
    try {
      const url = getStreamUrl("/monitor/vm/docker-stats-stream/", { interval: 2 });
      console.log("[SSE] Connecting to docker stats stream:", url);

      if (dockerStatsEventSourceRef.current) {
        dockerStatsEventSourceRef.current.close();
      }

      const es = new EventSource(url);
      dockerStatsEventSourceRef.current = es;
      setContainerStreamMode("sse");

      es.onmessage = (e) => {
        try {
          console.log("[SSE] Received message:", e.data?.substring(0, 100));
          const data = JSON.parse(e.data);
          // Backend sends raw array, not wrapped in object
          if (Array.isArray(data)) {
            console.log("[SSE] Updated containers:", data.length);
            setContainers(data);
            setLoading(false);
          } else if (data.containers && Array.isArray(data.containers)) {
            console.log("[SSE] Updated containers:", data.containers.length);
            setContainers(data.containers);
            setLoading(false);
          } else if (data.error) {
            console.error("[SSE] Backend error:", data.error);
          }
        } catch (err) {
          console.error("[SSE] Parse error:", err);
        }
      };

      es.onerror = () => {
        console.error("[SSE] Connection error, state:", es.readyState);
        try {
          es.close();
        } catch (e) {}
        dockerStatsEventSourceRef.current = null;
        setContainerStreamMode(null);
      };

      es.onopen = () => {
        console.log("[SSE] Connection opened");
      };
    } catch (err) {
      console.error("[SSE] Failed to open connection:", err);
      setContainerStreamMode(null);
    }

    return () => {
      if (dockerStatsEventSourceRef.current) {
        try {
          console.log("[SSE] Closing connection on unmount");
          dockerStatsEventSourceRef.current.close();
        } catch (e) {}
        dockerStatsEventSourceRef.current = null;
      }
      setContainerStreamMode(null);
    };
  }, []);

  if (loading) return <Loader />;

  if (error)
    return (
      <ErrorMsg
        msg={error}
        onRetry={load}
      />
    );

  return (
    <div>
      <PageHeader
        title="Docker Containers"
        subtitle="Live container resource usage and health metrics"
      />

      <div className="vm-containers-page__toolbar">
        {containerStreamMode === "sse" && (
          <div className="vm-containers-page__live-badge" title="Live docker stats streaming">
            ● Live
          </div>
        )}
        <button
          onClick={load}
          style={refreshBtnStyle}
        >
          ↻ Refresh
        </button>
      </div>

      <SectionCard
        title={`Running Containers (${containers.length})`}
      >
        <div className="vm-containers-page__table-wrap">
          <table className="vm-containers-page__table">
            <thead className="vm-containers-page__table-head">
              <tr>
                <th className="vm-containers-page__table-head-cell">Container</th>
                <th className="vm-containers-page__table-head-cell">CPU</th>
                <th className="vm-containers-page__table-head-cell">Memory</th>
                <th className="vm-containers-page__table-head-cell">Memory %</th>
                <th className="vm-containers-page__table-head-cell">Network</th>
                <th className="vm-containers-page__table-head-cell">PIDs</th>
                <th className="vm-containers-page__table-head-cell">Logs</th>
              </tr>
            </thead>

            <tbody>
              {containers.map((container) => (
                <tr
                  key={container.container_id}
                  style={{
                    borderBottom:
                      "1px solid var(--border)",
                  }}
                >
                  <td className="vm-containers-page__table-cell">
                    <div style={{ fontWeight: 600, color: "var(--text)" }}>
                      <button onClick={() => loadContainerDetails(container.name, "general")} className="vm-containers-page__name-btn">
                        {container.name}
                      </button>
                    </div>

                    <div className="vm-containers-page__table-cell--muted">
                      {container.container_id.substring(0, 12)}
                    </div>
                  </td>

                  <td className="vm-containers-page__table-cell">
                    <button onClick={() => loadContainerDetails(container.name, "cpu")} className="vm-containers-page__metric-btn">
                      <span className="vm-containers-page__metric-value" style={{ color: parseFloat(container.cpu) > 10 ? "#ef4444" : "#22c55e" }}>
                        {container.cpu}
                      </span>
                    </button>
                  </td>

                  <td className="vm-containers-page__table-cell">
                    <button onClick={() => loadContainerDetails(container.name, "memory")} className="vm-containers-page__metric-btn">
                      {container.memory_usage}
                    </button>
                  </td>

                  <td className="vm-containers-page__table-cell">
                    <button onClick={() => loadContainerDetails(container.name, "memory_pct")} className="vm-containers-page__metric-btn">
                      <div className="vm-containers-page__metric-bar">
                        <div className="vm-containers-page__metric-bar-track">
                          <div className="vm-containers-page__metric-bar-fill" style={{ width: container.memory_pct, background: "#3b82f6" }} />
                        </div>
                        <div className="vm-containers-page__metric-bar-label">{container.memory_pct}</div>
                      </div>
                    </button>
                  </td>

                  <td className="vm-containers-page__table-cell">
                    <button onClick={() => loadContainerDetails(container.name, "network")} className="vm-containers-page__metric-btn">
                      {container.net_io}
                    </button>
                  </td>

                  <td className="vm-containers-page__table-cell">
                    <button onClick={() => loadContainerDetails(container.name, "pids")} className="vm-containers-page__metric-btn">
                      {container.pids}
                    </button>
                  </td>

                  <td className="vm-containers-page__table-cell">
                    <button onClick={() => loadContainerLogs(container.name)} className="vm-containers-page__log-btn">
                      View Logs
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {containers.length === 0 && (
            <div className="vm-containers-page__empty">
              No containers found.
            </div>
          )}
        </div>
      </SectionCard>

      {(showLogs || showDetails) && (
        <div className="vm-containers-page__modal-overlay" onMouseDown={closeModal} role="presentation">
          <div
            className="vm-containers-page__modal"
            role="dialog"
            aria-modal="true"
            aria-label={showLogs ? `Logs for ${selectedContainer}` : `Details for ${selectedContainer}`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="vm-containers-page__modal-header">
              <div>
                <div className="vm-containers-page__modal-title">
                  {showDetails
                    ? selectedMetric === "cpu"
                      ? "CPU Details"
                      : selectedMetric === "memory"
                      ? "Memory Details"
                      : selectedMetric === "memory_pct"
                      ? "Memory % Details"
                      : selectedMetric === "network"
                      ? "Network Details"
                      : selectedMetric === "pids"
                      ? "PID Details"
                      : "Container Details"
                      : "Container Details"}
                    <span
                      title={
                        "Configured limits (docker inspect) vs Live usage (docker stats). Live stats update while modal is open."
                      }
                      aria-label="info"
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: "var(--muted)",
                        cursor: "help",
                      }}
                    >
                      ⓘ
                    </span>
                    {streamMode && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          padding: "4px 8px",
                          borderRadius: 8,
                          background: streamMode === "sse" ? "#d1fae5" : "#fffbeb",
                          color: streamMode === "sse" ? "#065f46" : "#92400e",
                          fontWeight: 700,
                          display: "inline-block",
                        }}
                        title={streamMode === "sse" ? "Live updates via SSE" : "Polling fallback active"}
                      >
                        {streamMode === "sse" ? "Live" : "Polling"}
                      </span>
                    )}
                </div>
                <div className="vm-containers-page__modal-subtitle">
                  {selectedContainer}
                </div>
              </div>

              <button
                onClick={closeModal}
                className="vm-containers-page__modal-close"
              >
                Close
              </button>
            </div>

            <div className="vm-containers-page__modal-body">
              {showLogs ? (
                logsLoading ? (
                  <Loader />
                ) : (
                  <pre className="vm-containers-page__log-view">
                    {logs.length > 0 ? logs.join("\n") : "No logs available"}
                  </pre>
                )
              ) : showDetails ? (
                detailsLoading ? (
                  <Loader />
                ) : details ? (
                  <div className="vm-containers-page__modal-body-inner">
                    {detailsError && (
                      <div className="vm-containers-page__modal-error">
                        {detailsError}
                      </div>
                    )}

                    {selectedMetric === "cpu" ? (
                      <div>
                        {details.stats && (
                          <div className="vm-containers-page__detail-section">
                            <div className="vm-containers-page__detail-label">CPU Usage (live)</div>
                            <div style={{ fontSize: 20, fontWeight: 700 }}>
                              {details.stats?.cpu_perc || details.stats?.cpu || "-"}
                            </div>
                          </div>
                        )}

                        <div className="vm-containers-page__modal-grid-2">
                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">CPU Shares</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.CpuShares)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">CPU Quota</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.CpuQuota)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">CPU Period</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.CpuPeriod)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Nano CPUs</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.NanoCpus)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">CPU Set</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.CpusetCpus)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">CPU Set Memory</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.CpusetMems)}</div>
                          </div>
                        </div>
                      </div>
                    ) : selectedMetric === "memory" || selectedMetric === "memory_pct" ? (
                      <div>
                        {details.stats && (
                          <div className="vm-containers-page__detail-section">
                            <div className="vm-containers-page__detail-label">Memory (live)</div>
                            <div style={{ fontSize: 16, fontWeight: 700 }}>
                              {details.stats?.memory_usage || "-"}
                              {details.stats?.memory_pct ? ` — ${details.stats.memory_pct}` : ""}
                            </div>
                          </div>
                        )}

                        <div className="vm-containers-page__modal-grid-2">
                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Memory Limit</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.Memory)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Memory Reservation</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.MemoryReservation)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Memory Swap</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.MemorySwap)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Kernel Memory</div>
                            <div>{formatConfigValue(details.raw?.HostConfig?.KernelMemory)}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">OOM Kill Disable</div>
                            <div>{details.raw?.HostConfig?.OomKillDisable ? "Yes" : "No"}</div>
                          </div>
                        </div>
                      </div>
                    ) : selectedMetric === "network" ? (
                      <div className="vm-containers-page__detail-section">
                        <div className="vm-containers-page__detail-label">Network Settings</div>
                        <pre className="vm-containers-page__json">{JSON.stringify(details.network_settings, null, 2)}</pre>
                      </div>
                    ) : selectedMetric === "pids" ? (
                      <div className="vm-containers-page__modal-grid-2">
                        <div className="vm-containers-page__detail-card">
                          <div className="vm-containers-page__detail-label">PID Limit</div>
                          <div>{formatConfigValue(details.raw?.HostConfig?.PidsLimit)}</div>
                        </div>

                        <div className="vm-containers-page__detail-card">
                          <div className="vm-containers-page__detail-label">PIDs Current</div>
                          <div>{details.state?.Pid ?? "-"}</div>
                        </div>

                        <div className="vm-containers-page__detail-section">
                          <div className="vm-containers-page__detail-label">Process State</div>
                          <pre className="vm-containers-page__json">{JSON.stringify(details.state, null, 2)}</pre>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {details.stats && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                              gap: 12,
                            }}
                          >
                            <div style={detailCardStyle}>
                              <div style={detailLabelStyle}>CPU Usage</div>
                              <div>{details.stats?.cpu_perc || details.stats?.cpu || "-"}</div>
                            </div>

                            <div style={detailCardStyle}>
                              <div style={detailLabelStyle}>Memory</div>
                              <div>{details.stats?.memory_usage || "-"}</div>
                            </div>

                            <div style={detailCardStyle}>
                              <div style={detailLabelStyle}>Memory %</div>
                              <div>{details.stats?.memory_pct || "-"}</div>
                            </div>

                            <div style={detailCardStyle}>
                              <div style={detailLabelStyle}>Network I/O</div>
                              <div>{details.stats?.net_io || "-"}</div>
                            </div>
                          </div>
                        )}

                        <div className="vm-containers-page__modal-grid-2">
                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Container ID</div>
                            <div>{details.container_id || "-"}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Image</div>
                            <div>{details.image || "-"}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Command</div>
                            <div>{details.command || "-"}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Created</div>
                            <div>{details.created || "-"}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">State</div>
                            <div>{details.state?.Status || "-"}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Running</div>
                            <div>{details.state?.Running ? "Yes" : "No"}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Exit Code</div>
                            <div>{details.state?.ExitCode ?? "-"}</div>
                          </div>

                          <div className="vm-containers-page__detail-card">
                            <div className="vm-containers-page__detail-label">Started At</div>
                            <div>{details.state?.StartedAt || "-"}</div>
                          </div>

                          <div className="vm-containers-page__detail-section">
                            <div className="vm-containers-page__detail-label">Network Settings</div>
                            <pre className="vm-containers-page__json">{JSON.stringify(details.network_settings, null, 2)}</pre>
                          </div>

                          <div className="vm-containers-page__detail-section">
                            <div className="vm-containers-page__detail-label">Mounts</div>
                            <pre className="vm-containers-page__json">{JSON.stringify(details.mounts, null, 2)}</pre>
                          </div>

                          {/* <div style={detailSectionStyle}>
                            <div style={detailLabelStyle}>Config</div>
                            <pre style={jsonStyle}>{JSON.stringify(details.config, null, 2)}</pre>
                          </div> */}

                          <div className="vm-containers-page__detail-section">
                            <div className="vm-containers-page__detail-label">Raw Inspect Output</div>
                            <pre className="vm-containers-page__json">{JSON.stringify(details.raw, null, 2)}</pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>No details available.</div>
                )
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const th: React.CSSProperties = {
  padding: "12px",
  textAlign: "left",
  fontSize: 12,
  color: "var(--muted)",
  fontWeight: 600,
};

const td: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text)",
  verticalAlign: "middle",
};

const nameButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  color: "var(--text)",
  cursor: "pointer",
  textDecoration: "underline",
  fontSize: "inherit",
  fontFamily: "inherit",
};

const metricButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  width: "100%",
  textAlign: "left",
  cursor: "pointer",
  color: "inherit",
};

const detailCardStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 12,
  background: "var(--surface-soft)",
  border: "1px solid var(--border)",
};

const detailLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--muted)",
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const detailSectionStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 12,
  background: "var(--surface-soft)",
  border: "1px solid var(--border)",
};

const jsonStyle: React.CSSProperties = {
  margin: 0,
  padding: 16,
  borderRadius: 8,
  background: "#111827",
  color: "#f8fafc",
  overflowX: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: 12,
  lineHeight: 1.5,
  fontFamily: "Consolas, Monaco, monospace",
};

export default VMContainersPage;
